/**
 * Reads the `audience`/`contactId` claims off a session JWT (Platform
 * Architecture V2 §4.6/§7) without verifying the signature — verification is
 * the backend's job; the frontend only needs the claims to decide which
 * shell to render (staff vs. client) and to key portal storage.
 */
export interface SessionClaims {
  audience: 'staff' | 'client'
  contactId?: number
  exp?: number
  [key: string]: unknown
}

function base64UrlDecode(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/').padEnd(segment.length + ((4 - (segment.length % 4)) % 4), '=')
  return atob(padded)
}

/** Pure — returns null for anything that doesn't parse as a 3-part JWT. */
export function decodeSessionClaims(jwt: string): SessionClaims | null {
  const parts = jwt.split('.')
  if (parts.length !== 3 || !parts[1]) return null
  try {
    const json = decodeURIComponent(
      Array.from(base64UrlDecode(parts[1]))
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    )
    const claims = JSON.parse(json) as unknown
    if (!claims || typeof claims !== 'object') return null
    const audience = (claims as Record<string, unknown>).audience
    if (audience !== 'staff' && audience !== 'client') return null
    return claims as SessionClaims
  } catch {
    return null
  }
}
